import RequestError from './HttpError';

export type Map = Record<string, string | boolean | number>;

type FetchFunction = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

const stringifyMap = (map: Map): Record<string, string> => Object.entries(map)
  .map(([key, value]) => [key, `${<string>value}`])
  .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

interface HttpClientOptions {
  apiUrl : string;
  altFetchMethodName? : string;
  accessToken? : string;
  traceid?: string;
}

class HttpClient {
  apiUrl: string;
  accessToken?: string;
  altFetchMethodName?: string;
  traceid?: string;

  public constructor({
    apiUrl, altFetchMethodName, accessToken, traceid,
  } : HttpClientOptions) {
    this.apiUrl = apiUrl;
    this.accessToken = accessToken;
    this.altFetchMethodName = altFetchMethodName;
    this.traceid = traceid ?? crypto.randomUUID();
  }

  public get<TResponse, TError>(path: string, query: Map = {}, headers: Map = {}): Promise<TResponse> {
    return this.fetch<undefined, TResponse, TError>('GET', path, query, headers);
  }

  public async post<TBody, TError>(path: string, body: TBody, query: Map = {}, headers: Map = {}): Promise<void> {
    return this.fetch<TBody, void, TError>('POST', path, query, headers, body);
  }

  private get defaultHeaders() : Map {
    const defaultHeaders : Map = {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=utf-8',
      'tef-coms-traceid': this.traceid ?? crypto.randomUUID(),
    };

    if (this.accessToken) {
      defaultHeaders.Authorization = `Bearer ${this.accessToken}`;
    }

    return defaultHeaders;
  }

  public async fetch<TBody, TResponse, TError>(
    method: 'POST' | 'GET',
    path: string,
    query: Map = {},
    headers: Map = {},
    body?: TBody,
  ): Promise<TResponse> {
    const params = new URLSearchParams(
      Object.entries(query).map(
        ([key, value]) => [key, value.toString()],
      ),
    );
    const qs = params.toString();
    const qsPrefixed = qs ? `?${qs}` : '';

    // @ts-expect-error "undefined is checked"
    const afetch: FetchFunction = (window[this.altFetchMethodName] ?? fetch) as FetchFunction;

    const extendedHeaders = stringifyMap({
      ...this.defaultHeaders,
      ...headers,
    });

    const response = await afetch(`${this.apiUrl}${path}${qsPrefixed}`, {
      method,
      headers: extendedHeaders,
      mode: 'cors',
      credentials: 'same-origin',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    if (!response.ok) {
      const { status, statusText } = response;
      const errorBody = await response.json() as TError;
      throw new RequestError(status, statusText, errorBody);
    }

    return method === 'GET' ? response.json() : undefined;
  }
}

export default HttpClient;
